import { css } from '@emotion/react'
import { format } from 'date-fns'
import { ko } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { notificationApi, type NotificationsResponse } from '~/@common/api/notificationApi'
import { errorHandlerService, webviewService } from '~/@common/services'
import { commonRepo } from '~/@common/services/repo.service'
import { typo } from '~/@common/styles'

const NotificationPage = () => {
  const [notifications, setNotifications] = useState<NotificationsResponse['result'] | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!commonRepo.academy?.academyId || !commonRepo.student?.id) return
      try {
        const { data } = await notificationApi.getNotifications({
          partitionKey: `${commonRepo.academy?.academyId}.${commonRepo.student.id}`,
          params: {
            size: 30,
            searchType: 'AFTER',
          },
        })
        setNotifications(data.result)
        webviewService.clearAppBadges()
      } catch (error) {
        errorHandlerService.handle(error)
      }
    }
    fetchNotifications()
  }, [])

  if (notifications && notifications.length === 0) {
    return <div css={S.Empty}>아직 알림이 없어요.</div>
  }

  const handleNotificationClick = (notification: NotificationsResponse['result'][number]) => {
    // 선생님앱의 notification response 형식과 맞추기 위해 아래 형식으로 link 추출
    const link = notification.data.cta[0].data.link
    if (!link) return
    navigate(link)
  }

  const formatNotificationDateTime = (dateTime: string): string => {
    const parsedDate = new Date(dateTime)
    if (Number.isNaN(parsedDate.getTime())) return dateTime
    return format(parsedDate, 'yyyy-MM-dd a h:mm', { locale: ko })
  }

  return (
    <div css={S.Container}>
      {notifications &&
        notifications.map((notification) => (
          <div
            className="notification-item"
            key={notification.sortKey}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="notification-item-title">{notification.data.payload.message}</div>
            <div className="notification-item-time">
              {formatNotificationDateTime(notification.createDateTime)}
            </div>
          </div>
        ))}
    </div>
  )
}

export default NotificationPage

const S = {
  Container: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    gap: 10px;
    overflow-y: auto;

    .notification-item {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 600px;
      padding: 14px 30px;
      background-color: var(--White);
      gap: 2px;
      border-radius: var(--Radius-300);
      cursor: pointer;

      .notification-item-title {
        ${typo.body02}
        font-weight: 700;
        color: var(--Colors-Gray-800);
        white-space: pre-line;
      }
      .notification-item-time {
        font-size: 12px;
      }
    }
  `,
  Empty: css`
    ${typo.body01}
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--Colors-Gray-900);
  `,
}
